// Models
import { ODataVersion } from "../../../service/planner/common/IOdataEntityResource";
import { PatchableODataEntity } from "./PatchableODataEntity";
// #endregion
/**
 * Base class for entities that support both patching and delta synchronization.
 *
 * This class extends PatchableODataEntity and adds delta sync capabilities:
 * - Applying delta changes received from the service
 * - Converting delta resources to entity updates
 * - Handling sub-entity delta arrays with add/update/delete operations
 *
 * @template T - The entity interface type
 * @template TU - The update resource type
 * @template TD - The delta resource type
 * @template TMutableKeys - The keys of T that are mutable
 */ export class PatchableAndDeltaSyncableODataEntity extends PatchableODataEntity {
    // #region Delta Sync API
    /**
     * Applies a delta resource to the current entity and returns the updated entity.
     * @param delta The delta resource to apply
     * @returns A new entity instance with the delta applied
     */ applyDelta(delta) {
        // Check if this is a delete operation
        if (this.isDeltaDelete(delta)) {
            throw new Error(`DeleteDeltaCannotBeApplied [Id=${this.id}]`);
        }
        // After the delete check, delta is narrowed to IODataEntityResource
        // Validate that the delta is for this entity
        if (delta.id !== this.id) {
            throw new Error(`DeltaIdMismatch [Id=${this.id}][DeltaId=${delta.id}]`);
        }
        // Convert delta resource to entity partial
        const partialEntity = this.mapDeltaChangesToEntity(delta);
        // Apply the partial entity to the current entity using applyDiffs
        return this.applyDiffs(partialEntity);
    }
    // #endregion
    // #region Property Mapping
    /**
     * Returns custom property mappings overrides that differ from the default 1:1 mapping.
     * Subclasses can override this method to provide custom mappings for specific properties.
     */ getPropertyMappingOverride() {
        return {};
    }
    /**
     * Returns the property mapping between entity properties and their change resource field names.
     * By default, creates a 1:1 mapping for all non-function properties of the entity interface,
     * then merges with overrides from getPropertyMappingOverride().
     *
     * Subclasses typically don't need to override this method. Instead, override getPropertyMappingOverride()
     * to provide custom mappings for specific properties that differ from the default.
     *
     * The default implementation creates 1:1 mappings for properties that exist on the instance.
     * This covers most common cases where property names match resource field names.
     */ getChangePropertyMapping() {
        const defaultMapping = {};
        // Create default 1:1 mapping for all readonly instance properties
        const instanceKeys = Object.keys(this);
        for (const key of instanceKeys){
            // Skip arrow functions stores as properties
            if (typeof this[key] === "function") {
                continue;
            }
            // Special handling for version property based on ODataVersion
            if (key === "version") {
                defaultMapping[key] = this.odataVersion === ODataVersion.Modern ? "@etag" : "@odata.etag";
            } else {
                // Create 1:1 mapping by default (property name -> property name)
                defaultMapping[key] = key;
            }
        }
        // Merge with custom mappings (custom mappings take precedence)
        return {
            ...defaultMapping,
            ...this.getPropertyMappingOverride()
        };
    }
    /**
     * Generic delta processor that builds a partial entity from delta resource.
     *
     * - For fields with custom translators: uses the translator function
     * - For fields without translators: directly assigns the value if present in delta
     * - Always includes id and version in the result
     *
     * @param delta The delta resource to process
     * @param customTranslators Map of entity property names to translator functions for custom transformations
     * @returns A partial entity with mapped delta values
     */ processDeltaWithTranslators(delta, customTranslators) {
        // Guard against delete deltas - this method should only process entity updates
        if (this.isDeltaDelete(delta)) {
            throw new Error("processDeltaWithTranslators cannot process delete deltas");
        }
        const resourceKeyMap = this.getChangePropertyMapping();
        const partial = {
            id: delta.id,
            version: this.odataVersion === ODataVersion.Modern ? delta["@etag"] : delta["@odata.etag"]
        };
        // Get all entity property keys from the mapping
        const entityKeys = Object.keys(resourceKeyMap);
        for (const entityKey of entityKeys){
            // Skip id and version as they're already handled
            if (entityKey === "id" || entityKey === "version") {
                continue;
            }
            const deltaKey = resourceKeyMap[entityKey];
            const deltaValue = delta[deltaKey];
            // Skip if delta doesn't contain this key
            if (deltaValue === undefined) {
                continue;
            }
            // Check if there's a custom translator for this property
            const translator = customTranslators[entityKey];
            if (translator) {
                // Use custom translator
                const translatedValue = translator(deltaValue, delta);
                if (translatedValue !== undefined) {
                    partial[entityKey] = translatedValue;
                }
            } else {
                // Direct assignment for simple mappings
                partial[entityKey] = deltaValue;
            }
        }
        return partial;
    }
    /**
     * Checks if a delta resource represents a delete operation
     * Type guard that narrows the type to IODataDeltaDeleteResource
     * @param delta The delta resource to check
     * @returns true if this is a delete delta, false otherwise
     */ isDeltaDelete(delta) {
        return "@removed" in delta;
    }
    // #endregion
    // #region Sub-Entity Delta Processing
    /**
     * Process a single sub-entity delta on an existing entity
     * Protected to allow subclasses to call from their delta mappers
     *
     * Does NOT support creating new entity if does not exist - it only processes
     * updates and delete on the existing entity.
     *
     * @param delta The delta resource for the sub-entity
     * @param existingEntity The existing entity to apply the delta to (or undefined for new/deleted entities)
     * @returns The updated sub-entity or undefined if entity should be removed
     */ applySubEntityDelta(delta, existingEntity) {
        // Handle delete
        if (this.isDeltaDelete(delta)) {
            return undefined;
        }
        // Apply delta to existing entity
        if (existingEntity && "applyDelta" in existingEntity && typeof existingEntity.applyDelta === "function") {
            return existingEntity.applyDelta(delta);
        }
        // No existing entity and create from delta is not supported
        throw new Error(`SubEntityDeltaApplyFailed: Create sub-entity not supported [Id=${delta.id}]`);
    }
    /**
     * Process a sub-entity array delta to update a list of sub-entities.
     *
     * This method efficiently applies delta changes to an array of sub-entities by:
     * 1. Creating a map of current entities indexed by ID
     * 2. Processing each delta in the array:
     *    - For delete operations (deltas with @removed): removes the entity from the map
     *    - For update operations: applies the delta to the existing entity using applySubEntityDelta
     * 3. Returns the updated array only if changes were made, otherwise returns undefined
     *
     * The method does NOT support creating new entities from deltas - it only processes
     * updates and deletes for entities that already exist in the currentArray.
     *
     * Protected to allow subclasses to call from their delta mappers.
     *
     * @template S - The sub-entity type that extends IEntity and supports diffing
     * @template SD - The delta resource type for the sub-entity
     * @template TSyncEnabled - The sub-entity type with delta sync capabilities
     * @param deltaArray The array of delta resources to apply (can be undefined or empty)
     * @param currentArray The current array of sub-entities to update
     * @returns The updated array of sub-entities if changes were made, or undefined if no changes occurred
     */ applySubEntityArrayDelta(deltaArray, currentArray) {
        if (!deltaArray || deltaArray.length === 0) {
            return undefined;
        }
        if (!Array.isArray(currentArray)) {
            return undefined;
        }
        // Create a map of current entities by ID for efficient lookup
        const entityMap = new Map(currentArray.map((entity)=>[
                entity.id,
                entity
            ]));
        // Track if any changes were made
        let hasChanges = false;
        // Process each delta
        for (const delta of deltaArray){
            // Handle delete
            if (this.isDeltaDelete(delta)) {
                // For delete deltas, use @id instead of id
                const deleteId = delta["@id"];
                if (entityMap.has(deleteId)) {
                    entityMap.delete(deleteId);
                    hasChanges = true;
                }
                continue;
            }
            // After isDeltaDelete check, TypeScript knows delta is not a delete resource
            // Handle update for existing entities, create from delta is not supported
            const existingEntity = entityMap.get(delta.id);
            if (existingEntity) {
                // Update existing entity using applySubEntityDelta
                const updatedEntity = this.applySubEntityDelta(delta, existingEntity);
                if (updatedEntity) {
                    entityMap.set(delta.id, updatedEntity);
                    hasChanges = true;
                }
            } else {
                throw new Error(`SubEntityArrayDeltaApplyFailed: Create sub-entity not supported [Id=${delta.id}]`);
            }
        }
        // Return updated array if changes were made
        if (hasChanges) {
            return Array.from(entityMap.values());
        }
        return undefined;
    }
    constructor(propertyBag, odataVersion = ODataVersion.Modern){
        super(propertyBag, odataVersion);
    }
}
